import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Breadcrumb, Button, Drawer } from "gold-ui-kit";
import { Download, Menu, HelpCircle, CalendarDays } from "lucide-react";
import { NAV_ITEMS, findNavGroup, findNavItem } from "../../data/navigation";
import { isPageDisabled } from "../../data/disabledPages";
import { Brand, NavList, UserCard } from "./Sidebar";

const MOBILE_TAB_IDS = ["dashboard", "personel", "devam", "istifa"];

const StatusBar = ({ onOpenHelp }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallPWA = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setDeferredPrompt(null);
  };

  const current = findNavItem(pathname);
  const group = findNavGroup(pathname);

  // GoldUI Breadcrumb renders plain <a href> links (full reload), so items navigate through the router instead.
  const crumb = (text, path) => (
    <span role="link" tabIndex={0} onClick={() => navigate(path)} onKeyDown={(e) => e.key === "Enter" && navigate(path)}>
      {text}
    </span>
  );
  const breadcrumbItems = [
    { text: crumb("ALTINGROSS", "/dashboard") },
    ...(group && group.title !== "Genel" ? [{ text: group.title }] : []),
    ...(current && current.id !== "dashboard" ? [{ text: current.label }] : [{ text: "Genel Bakış" }]),
  ];
  const todayStr = new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" });
  const mobileTabs = NAV_ITEMS.filter((m) => MOBILE_TAB_IDS.includes(m.id) && !isPageDisabled(m.path));

  return (
    <>
      <header
        className="h-14 shrink-0 bg-white/85 backdrop-blur-xl border-b border-slate-200/80 px-3 sm:px-6 flex items-center justify-between gap-3 z-30"
        data-testid="status-bar"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Button
            iconOnly
            size="sm"
            variant="text"
            color="dark"
            className="md:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label="Menüyü Aç"
          >
            <Menu size={20} />
          </Button>
          <div className="min-w-0">
            <Breadcrumb className="hidden !text-[11px] sm:flex" variant="muted" items={breadcrumbItems} />
            <h1 className="truncate text-[15px] font-bold tracking-tight text-slate-900">
              {current?.label || "Şube Yönetimi"}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="hidden lg:flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600">
            <CalendarDays size={14} className="text-slate-400" />
            <span>{todayStr}</span>
          </div>

          {deferredPrompt && (
            <Button size="sm" color="warning" radius="lg" onClick={handleInstallPWA} data-testid="pwa-install-btn">
              <span className="flex items-center gap-1.5">
                <Download size={14} />
                <span className="hidden sm:inline">Uygulamayı İndir</span>
              </span>
            </Button>
          )}

          <Button
            size="sm"
            variant="filled"
            color="default"
            radius="lg"
            onClick={onOpenHelp}
            data-testid="menubar-help-btn"
            title="Sayfa Dokümantasyonu ve Yardım"
          >
            <span className="flex items-center gap-1.5">
              <HelpCircle size={15} />
              <span className="hidden sm:inline">Yardım</span>
            </span>
          </Button>
        </div>
      </header>

      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 flex items-center justify-around py-1 px-1 md:hidden shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: "calc(0.25rem + env(safe-area-inset-bottom, 0px))" }}
      >
        {mobileTabs.map((m) => {
          const active = pathname === m.path;
          const Icon = m.icon;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => navigate(m.path)}
              className={`flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-all active:scale-95 flex-1 ${
                active ? "text-amber-600 font-bold" : "text-slate-500"
              }`}
            >
              <span className={`p-1 rounded-full ${active ? "bg-amber-50" : ""}`}>
                <Icon size={19} />
              </span>
              <span className="text-[10px] mt-0.5 font-medium tracking-tight">{m.shortLabel}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="flex flex-col items-center justify-center py-1 px-2 rounded-xl transition-all active:scale-95 flex-1 text-slate-500"
        >
          <span className="p-1">
            <Menu size={19} />
          </span>
          <span className="text-[10px] mt-0.5 font-medium tracking-tight">Tüm Menü</span>
        </button>
      </nav>

      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="left"
        size={85}
        variant="dark"
        title={<Brand onClick={() => navigate("/dashboard")} />}
        className="md:hidden !w-[300px] !max-w-[85vw] !bg-slate-950 !border-slate-900 text-white [&>div:first-child]:!border-slate-800"
      >
        {({ requestClose }) => (
          <div className="flex h-full flex-col justify-between gap-6">
            <NavList onNavigate={requestClose} />
            <UserCard />
          </div>
        )}
      </Drawer>
    </>
  );
};

export default StatusBar;
